import { memo } from "react";
import { ResponsiveLine, SliceTooltipProps } from "@nivo/line";
import { Chip } from "@nivo/tooltip";
import { useTheme } from "@nivo/core";

const theme = {
  text: {
    color: "#8B8D97",
    fontSize: "13px",
    fontFamily: "Satoshi"
  },
  axis: {
    ticks: {
      line: {
        strokeWidth: 0
      },
      text: {
        fill: "#8B8D97",
        fontSize: "13px",
        fontFamily: "Satoshi"
      }
    },
    domain: {
      line: {
        stroke: "rgba(28,29,34,0.100)"
      }
    }
  },
  tooltip: {
    chip: {
      borderRadius: "50%",
      marginRight: "0px",
      height: "8px",
      width: "8px"
    },
    tableCell: {
      fontSize: "13px",
      color: "#1C1D22"
    },
    container: {
      borderRadius: "4px"
    }
  },
  crosshair: {
    line: {
      stroke: "rgba(28,29,34,0.300)",
      strokeWidth: 1,
      strokeDasharray: "2 2"
    }
  },
  grid: {
    line: {
      stroke: "rgba(28,29,34,0.100)",
      strokeWidth: 1,
      strokeDasharray: "2 2"
    }
  }
};

const ChartTableTooltip = memo(({ slice, axis }: SliceTooltipProps) => {
  const theme = useTheme();
  const otherAxis = axis === "x" ? "y" : "x";
  const rows = slice.points.map((point) => [
    <Chip key="chip" color={point.serieColor} style={theme.tooltip.chip} />,
    point.serieId,
    <span key="value" style={theme.tooltip.tableCellValue}>
      {point.data[`${otherAxis}Formatted`]}
    </span>
  ]);

  return (
    <div style={theme.tooltip.container}>
      <table style={theme.tooltip.table} className="w-full">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((column, j) => (
                <td
                  key={j}
                  style={theme.tooltip.tableCell}
                  className="max-w-xs xl:max-w-sm 2xl:max-w-lg 3xl:max-w-xl"
                >
                  {column}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
ChartTableTooltip.displayName = "ChartTableTooltip";

export const Chart = ({ data }: any) => {
  return (
    <ResponsiveLine
      data={data}
      curve="linear"
      animate
      enableSlices="x"
      sliceTooltip={({ slice, axis }) => (
        <ChartTableTooltip slice={slice} axis={axis} />
      )}
      margin={{ top: 10, right: 30, bottom: 50, left: 70 }}
      yScale={{
        stacked: true,
        type: "linear"
      }}
      yFormat={(value) => {
        const formattedValue = value.toLocaleString("en-US", {
          style: "currency",
          currency: "USD"
        });
        return formattedValue;
      }}
      theme={theme}
      enableGridX={false}
      gridYValues={5}
      axisLeft={{
        tickValues: 3,
        format: (value) => {
          const formattedValue = value.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0
          });
          return formattedValue;
        }
      }}
      axisBottom={{
        tickRotation: data[0]?.data?.length > 15 ? -45 : 0
      }}
      lineWidth={1}
      pointSize={7}
      enableArea
      colors={(d) => d.color}
      areaOpacity={0.05}
    />
  );
};
